import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { transactionsAPI, recipientsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, Clock, CheckCircle, XCircle } from 'lucide-react';

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [recipients, setRecipients] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [txRes, recipientsRes] = await Promise.all([
        transactionsAPI.getAll(),
        recipientsAPI.getAll()
      ]);
      setTransactions(txRes.data);
      
      const recipientsMap = {};
      recipientsRes.data.forEach(r => {
        recipientsMap[r.id] = r;
      });
      setRecipients(recipientsMap);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': 
      case 'payment_processed': return <CheckCircle className="w-5 h-5 text-accent" />;
      case 'funds_sent': return <CheckCircle className="w-5 h-5 text-sky-500" />;
      case 'pending': 
      case 'awaiting_funds':
      case 'processing': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed': 
      case 'canceled': return <XCircle className="w-5 h-5 text-destructive" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
      case 'payment_processed': 
      case 'funds_sent': return 'default';
      case 'pending':
      case 'awaiting_funds':
      case 'processing': return 'secondary';
      case 'failed':
      case 'canceled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'funds_sent': 'Funds Sent',
      'awaiting_funds': 'Awaiting Funds',
      'payment_processed': 'Completed',
      'completed': 'Completed',
      'pending': 'Pending',
      'processing': 'Processing',
      'failed': 'Failed',
      'canceled': 'Canceled',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Transaction History</h1>
          <p className="text-muted-foreground">View all your money transfer transactions</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : transactions.length === 0 ? (
          <Card data-testid="no-transactions-card" className="p-12 text-center">
            <p className="text-muted-foreground mb-2">No transactions yet</p>
            <p className="text-sm text-muted-foreground">Your transaction history will appear here</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {transactions.map((tx) => {
              const recipient = recipients[tx.recipient_id];
              const isBridge = tx.provider === 'bridge';
              const displayStatus = tx.state || tx.status;
              return (
                <Card key={tx.id} data-testid={`transaction-card-${tx.id}`} className="p-6 hover:shadow-md transition-all">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getStatusIcon(displayStatus)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">
                            {isBridge 
                              ? `${tx.transfer_type === 'onramp' ? 'USD → USDC On-Ramp' : tx.from_currency + ' → ' + tx.to_currency}`
                              : (recipient ? recipient.name : 'Unknown Recipient')}
                          </h3>
                          <Badge data-testid={`transaction-status-${tx.id}`} variant={getStatusVariant(displayStatus)} className="capitalize">
                            {getStatusLabel(displayStatus)}
                          </Badge>
                          {isBridge && (
                            <Badge variant="outline" className="text-xs">Bridge</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {isBridge 
                            ? `Via ${(tx.source_rail || 'ACH').toUpperCase()} • Bridge.xyz`
                            : (recipient ? `${recipient.bank_name} • ${recipient.country}` : 'N/A')}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>

                    <div className="md:text-right">
                      <div className="flex items-center gap-2 justify-end mb-2">
                        <span className="font-mono font-bold text-2xl">${tx.amount?.toFixed(2)}</span>
                        <span className="text-muted-foreground">{tx.from_currency}</span>
                        <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {tx.fee != null && <p>Fee: <span className="font-mono">${tx.fee?.toFixed(2)}</span></p>}
                        {tx.total_amount != null && <p>Total: <span className="font-mono font-semibold">${tx.total_amount?.toFixed(2)} {tx.from_currency}</span></p>}
                        {isBridge && tx.usdmint_settled && (
                          <p className="text-accent">USDMint settled</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}